import React from 'react';
import { Row, Col, Card, Table, ButtonGroup, Button } from 'react-bootstrap';
import SearchSelect from './SearchSelect';
import { formatDay } from '../utils/dates';
import PagedRows from './PagedRows';

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const dateStr = (d) => formatDay(d);

const CobrosTab = ({
  outstanding, arAging, dso, payments,
  collections, collectionsGroupBy, onCollectionsGroupByChange,
  clients, selectedClientId, onSelectClient, statement,
  showCompanyKpis = true, // DSO and the collections summary are company-wide KPIs: admins only
}) => (
  <div className="mt-3">
    <Row className="mb-3">
      {showCompanyKpis && (
      <Col md={4}>
        <Card className="shadow-sm p-3 dashboard-card">
          <div className="text-muted small">Días Promedio de Pago (DSO)</div>
          <h2 className="mt-2 mb-0" style={{ color: 'var(--mm-cyan)' }}>
            {dso?.dso != null ? dso.dso.toFixed(1) : '—'}
          </h2>
          <div className="text-muted small">
            {dso?.sampleSize ? `sobre ${dso.sampleSize} facturas pagadas` : 'sin facturas pagadas en este rango'}
          </div>
        </Card>
      </Col>
      )}
      <Col md={showCompanyKpis ? 8 : 12}>
        <Card className="shadow-sm p-3 card-equal-height">
          <Card.Title>Antigüedad de Cuentas por Cobrar (Aging)</Card.Title>
          <PagedRows rows={arAging?.rows || []}>{(rows) => (
            <Table hover responsive size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th className="text-end">Al día</th>
                  <th className="text-end">1-30</th>
                  <th className="text-end">31-60</th>
                  <th className="text-end">61-90</th>
                  <th className="text-end">90+</th>
                  <th className="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.client}</td>
                    <td className="text-end">{money(r.current)}</td>
                    <td className="text-end">{money(r.d1_30)}</td>
                    <td className="text-end">{money(r.d31_60)}</td>
                    <td className="text-end" style={{ color: r.d61_90 > 0 ? 'var(--mm-yellow)' : undefined }}>{money(r.d61_90)}</td>
                    <td className="text-end" style={{ color: r.d90plus > 0 ? 'var(--mm-red)' : undefined }}>{money(r.d90plus)}</td>
                    <td className="text-end" style={{ fontWeight: 600 }}>{money(r.total)}</td>
                  </tr>
                ))}
                {(arAging?.rows || []).length === 0 && (
                  <tr><td colSpan={7} className="text-muted text-center">No hay cuentas por cobrar pendientes.</td></tr>
                )}
              </tbody>
              {arAging?.totals && (arAging.rows || []).length > 0 && (
                <tfoot>
                  <tr style={{ fontWeight: 700, borderTop: '2px solid var(--mm-line)' }}>
                    <td>Total</td>
                    <td className="text-end">{money(arAging.totals.current)}</td>
                    <td className="text-end">{money(arAging.totals.d1_30)}</td>
                    <td className="text-end">{money(arAging.totals.d31_60)}</td>
                    <td className="text-end">{money(arAging.totals.d61_90)}</td>
                    <td className="text-end">{money(arAging.totals.d90plus)}</td>
                    <td className="text-end">{money(arAging.totals.total)}</td>
                  </tr>
                </tfoot>
              )}
            </Table>
          )}</PagedRows>
        </Card>
      </Col>
    </Row>

    <Card className="shadow-sm p-3 mb-3">
      <Card.Title>Estado de Cuenta por Cliente</Card.Title>
      <div style={{ maxWidth: 360 }} className="mb-3">
        <SearchSelect
          options={clients}
          value={selectedClientId}
          getKey={c => c._id}
          getLabel={c => `${c.name}${c.rnc ? ' — RNC ' + c.rnc : ''}`}
          onSelect={c => onSelectClient(c._id)}
          placeholder="Buscar cliente..."
          emptyLabel="Sin clientes coincidentes"
        />
      </div>
      {statement ? (
        <>
          <div className="text-muted small mb-2">Facturas con saldo pendiente, de todo el historial del cliente.</div>
          <PagedRows rows={statement.invoices}>{(rows) => (
            <Table hover responsive size="sm" className="mb-2">
              <thead>
                <tr><th>Fecha</th><th>Factura</th><th>Descripción</th><th className="text-end">Total</th><th className="text-end">Pagado</th><th className="text-end">Balance</th></tr>
              </thead>
              <tbody>
                {rows.map((inv, i) => (
                  <tr key={i}>
                    <td>{dateStr(inv.date)}</td>
                    <td>{inv.number}</td>
                    <td>{inv.description}</td>
                    <td className="text-end">{money(inv.total)}</td>
                    <td className="text-end">{money(inv.amountPaid)}</td>
                    <td className="text-end" style={{ color: inv.balance < 0 ? 'var(--mm-green)' : undefined }}>{money(inv.balance)}</td>
                  </tr>
                ))}
                {statement.invoices.length === 0 && (
                  <tr><td colSpan={6} className="text-muted text-center">Sin facturas con saldo pendiente.</td></tr>
                )}
              </tbody>
            </Table>
          )}</PagedRows>
          <div className="d-flex justify-content-end">
            <div style={{ fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif" }}>
              Balance Total: <span style={{ color: 'var(--mm-cyan)' }}>{money(statement.totalBalance)}</span>
            </div>
          </div>
        </>
      ) : (
        <div className="text-muted">Selecciona un cliente para ver su estado de cuenta.</div>
      )}
    </Card>

    <Row className="mb-3">
      <Col md={showCompanyKpis ? 7 : 12}>
        <Card className="shadow-sm p-3 card-equal-height">
          <Card.Title>Registro de Pagos</Card.Title>
          <PagedRows rows={payments}>{(rows) => (
            <Table hover responsive size="sm" className="mb-0">
              <thead>
                <tr><th>Fecha</th><th>Factura</th><th>Cliente</th><th className="text-end">Monto</th><th>Método</th></tr>
              </thead>
              <tbody>
                {rows.map((p, i) => (
                  <tr key={i}>
                    <td>{dateStr(p.date)}</td><td>{p.invoiceNumber}</td><td>{p.client}</td>
                    <td className="text-end">{money(p.amount)}</td><td>{p.method}{p.account ? ` · ${p.account}` : ''}</td>
                  </tr>
                ))}
                {payments.length === 0 && <tr><td colSpan={5} className="text-muted text-center">Sin pagos en este rango.</td></tr>}
              </tbody>
            </Table>
          )}</PagedRows>
        </Card>
      </Col>
      {showCompanyKpis && (
      <Col md={5}>
        <Card className="shadow-sm p-3 card-equal-height">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Card.Title className="mb-0">Cobros</Card.Title>
            <ButtonGroup size="sm">
              <Button
                variant={collectionsGroupBy === 'day' ? 'primary' : 'outline-secondary'}
                onClick={() => onCollectionsGroupByChange('day')}
              >Día</Button>
              <Button
                variant={collectionsGroupBy === 'month' ? 'primary' : 'outline-secondary'}
                onClick={() => onCollectionsGroupByChange('month')}
              >Mes</Button>
            </ButtonGroup>
          </div>
          <PagedRows rows={collections}>{(rows) => (
            <Table hover size="sm" className="mb-0">
              <thead><tr><th>Período</th><th className="text-end">Total</th><th className="text-end"># Pagos</th></tr></thead>
              <tbody>
                {rows.map((c, i) => (
                  <tr key={i}><td>{c.period}</td><td className="text-end">{money(c.total)}</td><td className="text-end">{c.count}</td></tr>
                ))}
                {collections.length === 0 && <tr><td colSpan={3} className="text-muted text-center">Sin cobros en este rango.</td></tr>}
              </tbody>
            </Table>
          )}</PagedRows>
        </Card>
      </Col>
      )}
    </Row>

    <Card className="shadow-sm p-3">
      <Card.Title>Facturas Pendientes (detalle)</Card.Title>
      <PagedRows rows={outstanding}>{(rows) => (
        <Table hover responsive size="sm" className="mb-0">
          <thead>
            <tr><th>Factura</th><th>Cliente</th><th className="text-end">Total</th><th className="text-end">Pagado</th><th className="text-end">Adeudado</th><th>Vencimiento</th><th>Días vencido</th></tr>
          </thead>
          <tbody>
            {rows.map((o, i) => (
              <tr key={i}>
                <td>{o.number}</td><td>{o.client}</td>
                <td className="text-end">{money(o.total)}</td><td className="text-end">{money(o.amountPaid)}</td>
                <td className="text-end" style={{ color: 'var(--mm-yellow)', fontWeight: 600 }}>{money(o.owed)}</td>
                <td>{dateStr(o.dueDate)}</td>
                <td style={{ color: o.daysOverdue > 0 ? 'var(--mm-red)' : 'var(--mm-muted)' }}>{o.daysOverdue > 0 ? o.daysOverdue : '—'}</td>
              </tr>
            ))}
            {outstanding.length === 0 && <tr><td colSpan={7} className="text-muted text-center">No hay cuentas por cobrar pendientes.</td></tr>}
          </tbody>
        </Table>
      )}</PagedRows>
    </Card>
  </div>
);

export default CobrosTab;
